/**
 * ActivityDropdown Component
 *
 * Provides a dropdown for selecting activity time ranges for filtering repositories.
 * Used in the repository import flow to filter by recent activity.
 *
 * @module components/ui/ActivityDropdown
 */

import React, { useState, useRef, useEffect, useId } from 'react';
import PropTypes from 'prop-types';
import { Clock, ChevronDown, Check, X } from 'lucide-react';

// Activity filter options (all in days for consistency)
export const ACTIVITY_OPTIONS = [
  { value: 30, label: 'Last 30 days', shortLabel: '30d' },
  { value: 60, label: 'Last 60 days', shortLabel: '60d' },
  { value: 90, label: 'Last 90 days', shortLabel: '90d' },
  { value: 180, label: 'Last 6 months', shortLabel: '6mo' },
  { value: 270, label: 'Last 9 months', shortLabel: '9mo' },
  { value: 365, label: 'Last year', shortLabel: '1yr' },
  { value: null, label: 'All time', shortLabel: 'All' },
];

/**
 * ActivityDropdown Component
 *
 * @param {Object} props
 * @param {number|null} props.value - Current activity filter value (days) or null for all time
 * @param {function} props.onChange - Callback when value changes
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the dropdown is disabled
 * @param {string} props.size - Size variant ('sm' | 'md' | 'lg')
 * @param {boolean} props.showClear - Whether to show clear button when value is set
 * @param {boolean} props.compact - Use compact display (chip-like)
 */
const ActivityDropdown = ({
  value,
  onChange,
  className = '',
  disabled = false,
  size = 'md',
  showClear = true,
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const id = useId();
  const listboxId = `${id}-listbox`;

  // Find current option
  const currentOption = ACTIVITY_OPTIONS.find(opt => opt.value === value) || ACTIVITY_OPTIONS[ACTIVITY_OPTIONS.length - 1];
  const hasValue = value !== null;

  // Size styles
  const sizeStyles = {
    sm: {
      trigger: 'px-2 py-1 text-xs min-h-[32px]',
      option: 'px-2 py-1.5 text-xs',
      icon: 'h-3 w-3',
    },
    md: {
      trigger: 'px-3 py-2 text-sm min-h-[44px]',
      option: 'px-3 py-2 text-sm',
      icon: 'h-4 w-4',
    },
    lg: {
      trigger: 'px-4 py-2.5 text-base min-h-[48px]',
      option: 'px-4 py-2.5 text-base',
      icon: 'h-5 w-5',
    },
  };

  const styles = sizeStyles[size];

  // Handle toggle
  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        // Find current value index for highlighting
        const idx = ACTIVITY_OPTIONS.findIndex(opt => opt.value === value);
        setHighlightedIndex(idx >= 0 ? idx : 0);
      }
    }
  };

  // Handle select
  const handleSelect = (option) => {
    onChange?.(option.value);
    setIsOpen(false);
  };

  // Handle clear
  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.(null);
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (disabled) return;

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen && ACTIVITY_OPTIONS[highlightedIndex]) {
          handleSelect(ACTIVITY_OPTIONS[highlightedIndex]);
        } else {
          setIsOpen(true);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex(i =>
            i < ACTIVITY_OPTIONS.length - 1 ? i + 1 : 0
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex(i =>
            i > 0 ? i - 1 : ACTIVITY_OPTIONS.length - 1
          );
        }
        break;
      case 'Home':
        if (isOpen) {
          e.preventDefault();
          setHighlightedIndex(0);
        }
        break;
      case 'End':
        if (isOpen) {
          e.preventDefault();
          setHighlightedIndex(ACTIVITY_OPTIONS.length - 1);
        }
        break;
      default:
        break;
    }
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted option into view
  useEffect(() => {
    if (isOpen && listRef.current) {
      const highlighted = listRef.current.querySelector('[data-highlighted="true"]');
      if (highlighted) {
        highlighted.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Compact chip-like display
  if (compact) {
    return (
      <div ref={containerRef} className={`relative inline-block ${className}`}>
        <button
          type="button"
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          className={`
            inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg
            transition-colors min-h-[44px]
            ${hasValue
              ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
              : 'text-gray-400 hover:text-white hover:bg-white/5 border border-transparent'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <Clock className="h-4 w-4" aria-hidden="true" />
          <span>{currentOption.shortLabel}</span>
          {hasValue && showClear ? (
            <button
              type="button"
              onClick={handleClear}
              className="p-0.5 hover:bg-primary-500/30 rounded"
              aria-label="Clear activity filter"
            >
              <X className="h-3 w-3" />
            </button>
          ) : (
            <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          )}
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div
            ref={listRef}
            role="listbox"
            id={listboxId}
            className="absolute z-50 mt-1 w-40 bg-surface-elevated border border-white/10 rounded-lg shadow-xl overflow-hidden"
          >
            {ACTIVITY_OPTIONS.map((option, index) => {
              const isSelected = option.value === value;
              const isHighlighted = index === highlightedIndex;

              return (
                <div
                  key={option.value ?? 'null'}
                  role="option"
                  aria-selected={isSelected}
                  data-highlighted={isHighlighted}
                  onClick={() => handleSelect(option)}
                  className={`
                    ${styles.option}
                    cursor-pointer flex items-center justify-between
                    ${isHighlighted ? 'bg-white/10' : ''}
                    ${isSelected ? 'text-primary-400' : 'text-white'}
                    hover:bg-white/5
                  `}
                >
                  <span>{option.label}</span>
                  {isSelected && <Check className="h-4 w-4" />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Full dropdown display
  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        id={id}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-label="Select activity time range"
        className={`
          w-full ${styles.trigger}
          bg-white/5 border rounded-lg
          text-left flex items-center gap-2
          focus:outline-none focus:ring-2 focus:ring-primary-500
          focus:ring-offset-2 focus:ring-offset-gray-900
          transition-colors
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${isOpen ? 'border-primary-500' : 'border-white/10 hover:border-white/20'}
        `}
      >
        <Clock className={`${styles.icon} text-gray-400`} aria-hidden="true" />
        <span className={`flex-1 ${hasValue ? 'text-white' : 'text-gray-500'}`}>
          {currentOption.label}
        </span>

        {/* Clear button */}
        {hasValue && showClear && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="p-1 text-gray-400 hover:text-white rounded focus:outline-none"
            aria-label="Clear activity filter"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {/* Dropdown arrow */}
        <ChevronDown
          className={`${styles.icon} text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={listRef}
          role="listbox"
          id={listboxId}
          className="absolute z-50 w-full mt-1 bg-surface-elevated border border-white/10 rounded-lg shadow-xl overflow-hidden"
        >
          {ACTIVITY_OPTIONS.map((option, index) => {
            const isSelected = option.value === value;
            const isHighlighted = index === highlightedIndex;

            return (
              <div
                key={option.value ?? 'null'}
                role="option"
                aria-selected={isSelected}
                data-highlighted={isHighlighted}
                onClick={() => handleSelect(option)}
                className={`
                  ${styles.option}
                  cursor-pointer flex items-center justify-between
                  ${isHighlighted ? 'bg-white/10' : ''}
                  ${isSelected ? 'text-primary-400' : 'text-white'}
                  hover:bg-white/5
                `}
              >
                <div className="flex items-center gap-2">
                  <Clock className={styles.icon} aria-hidden="true" />
                  <span>{option.label}</span>
                </div>
                {isSelected && <Check className="h-4 w-4" />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

ActivityDropdown.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  showClear: PropTypes.bool,
  compact: PropTypes.bool,
};

/**
 * Helper function to filter items by activity date
 * @param {Array} items - Items to filter
 * @param {number|null} days - Number of days to look back, or null for all time
 * @param {string} dateField - Field name containing the date (default: 'pushed_at')
 * @returns {Array} Filtered items
 */
export const filterByActivity = (items, days, dateField = 'pushed_at') => {
  if (days === null) {
    return items;
  }

  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);

  return items.filter(item => {
    const itemDate = new Date(item[dateField]);
    return itemDate > cutoffDate;
  });
};

export default ActivityDropdown;
